import { useCallback, useEffect, useId, useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Riquadro, Scelta } from '../../componenti/base.tsx'
import { IconaCarica } from '../../componenti/Icone.tsx'
import { caricaDocumento } from '../../servizi/documenti.ts'
import { ETICHETTA_IN_VERIFICA, attesaAntivirus } from '../../servizi/regole-documenti.ts'
import type { SchedaLetta } from '../../servizi/regole-anagrafica.ts'
import {
  TIPO_MODULO_FIRMATO, type ModuliDellaScheda, type StatoModuloCidis,
} from '../../servizi/modulo-progetto.ts'
import { ModaleModuloFirmato, ModaleSottoscrizione, useModuloConsensi } from '../SchedaConsensi.tsx'
import { oggi } from './comune.ts'

/**
 * «Moduli dei progetti» nella scheda della persona (492): per ogni progetto dei suoi percorsi che ha un
 * modulo del destinatario, le due firme che servono (495, decisione di Davide del 31/10/2026: «la scheda
 * per titolare diverso è da firmare, poi deve firmare anche la scheda CIDIS»):
 *
 *  - «Modulo LUMEN: firmato il … / da firmare», dal modulo firmato caricato come documento della scheda,
 *    tipo «Modulo di progetto firmato», con il progetto e la data della firma; passa dall'antivirus come
 *    gli altri documenti. Il titolare del trattamento e' quello del progetto, che puo' essere un altro ente;
 *  - «Modulo CIDIS (consensi): firmato il … / da firmare», dal registro dei consensi: la scheda utente da
 *    firmare, con la scansione del modulo firmato (474). Si registra e si carica con le finestre della
 *    sezione Consensi; lo vede solo chi legge i consensi della scheda.
 *
 * Accanto a ciascuno «Stampa» e il caricamento; sotto, «Stampa i due moduli», uno dopo l'altro nella
 * stessa stampa (StampaModuloProgetto.tsx). Le schede con dati anonimizzati non hanno moduli: il
 * riquadro lo dice e basta. Per chi non vede il codice fiscale in chiaro le stampe restano disabilitate,
 * con il motivo.
 */

const messaggio = (e: unknown) => (e instanceof ErroreApi ? e.message
  : e instanceof Error ? `Caricamento non riuscito: ${e.message}` : 'Non è andata a buon fine: controlla la connessione e riprova.')

/** AAAA-MM-GG in GG/MM/AAAA, senza passare dal fuso. */
const giorno = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/')

type Progetto = ModuliDellaScheda['progetti'][number]
type Aperta =
  | { tipo: 'progetto'; progetto: string }
  | { tipo: 'consensi' }
  | { tipo: 'scansione' }

export function ModuliProgetto({ scheda, ricarica }: { scheda: SchedaLetta; ricarica: () => Promise<void> | void }) {
  const personaId = scheda.id
  const { puo } = useSessione()
  const [dati, setDati] = useState<ModuliDellaScheda | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [aperta, setAperta] = useState<Aperta | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const idMotivo = useId()

  // Si rilegge a ogni ricarica della scheda: i consensi registrati nella sezione sopra cambiano lo stato.
  const carica = useCallback(() => {
    api.get<ModuliDellaScheda>(`/persone/${personaId}/moduli-progetto`)
      .then((d) => { setDati(d); setErrore(null) })
      .catch((e) => setErrore(messaggio(e)))
  }, [personaId])
  useEffect(carica, [carica, scheda])

  // Le finestre dei consensi sono quelle della sezione Consensi: registra chi li registra nella scheda.
  const registrare = !!scheda.puo.registrareConsensi
  const caricareDocumenti = puo('documento.carica') && puo('documento.leggi')
  const { modulo: moduloConsensi } = useModuloConsensi(registrare && dati?.cidis?.stato === 'da_firmare')

  if (errore) return <Avviso tipo="errore">{errore}</Avviso>
  // Senza progetti con un modulo la sezione non c'e'.
  if (!dati || !dati.progetti.length) return null

  const conclusa = async (testo: string) => {
    setAperta(null); setEsito(testo)
    await ricarica()
  }

  return (
    <Riquadro titolo="Moduli dei progetti">
      {esito && <div role="status"><Avviso tipo="successo">{esito}</Avviso></div>}
      {dati.anonimizzata
        ? <p className="persona-nota-sezione">{dati.motivo}</p>
        : (
          <>
            <p className="persona-nota-sezione">
              Per una persona in un progetto con il suo modulo servono due firme: il modulo del progetto, con
              l’informativa del titolare del trattamento del progetto, e il modulo CIDIS dei consensi. Si stampano già
              compilati, anche insieme, si fanno firmare e si caricano da qui.
            </p>
            {!dati.stampare && dati.motivo && <p className="campo-aiuto" id={idMotivo}>{dati.motivo}</p>}
            {dati.progetti.map((p) => (
              <FirmeDelProgetto key={p.id} personaId={personaId} progetto={p} dati={dati} idMotivo={idMotivo}
                                caricaProgetto={() => { setEsito(null); setAperta({ tipo: 'progetto', progetto: p.id }) }}
                                registraConsensi={registrare && moduloConsensi
                                  ? () => { setEsito(null); setAperta({ tipo: 'consensi' }) } : null}
                                caricaScansione={registrare && caricareDocumenti && scheda.consensi
                                  ? () => { setEsito(null); setAperta({ tipo: 'scansione' }) } : null} />
            ))}
            <p className="tenue piccolo">I file dei moduli firmati si aprono dalla linguetta Documenti.</p>
          </>
          )}

      {aperta?.tipo === 'progetto' && (
        <Modale titolo="Carica il modulo del progetto firmato" chiudi={() => setAperta(null)}>
          <CaricaFirmato personaId={personaId} dati={dati} iniziale={aperta.progetto} annulla={() => setAperta(null)}
                         fatto={(testo) => { setAperta(null); setEsito(testo); carica() }} />
        </Modale>
      )}
      {aperta?.tipo === 'consensi' && moduloConsensi && (
        <ModaleSottoscrizione personaId={personaId} modulo={moduloConsensi} caricare={caricareDocumenti}
                              chiudi={() => setAperta(null)} conclusa={conclusa} />
      )}
      {aperta?.tipo === 'scansione' && scheda.consensi && (
        <ModaleModuloFirmato personaId={personaId} modulo={scheda.consensi.modulo_firmato}
                             chiudi={() => setAperta(null)} conclusa={conclusa} />
      )}
    </Riquadro>
  )
}

/** Le due righe di stato di un progetto, con i comandi, e «Stampa i due moduli». */
function FirmeDelProgetto({ personaId, progetto: p, dati, idMotivo, caricaProgetto, registraConsensi, caricaScansione }: {
  personaId: string
  progetto: Progetto
  dati: ModuliDellaScheda
  idMotivo: string
  caricaProgetto: () => void
  /** Nulli per chi non registra i consensi o non carica documenti. */
  registraConsensi: (() => void) | null
  caricaScansione: (() => void) | null
}) {
  const stampa = (indirizzo: string, etichetta: string, testo = 'Stampa') => (dati.stampare
    ? <Link className="bottone bottone-secondario bottone-piccolo" to={indirizzo} aria-label={etichetta}>{testo}</Link>
    : (
      <Bottone variante="secondario" dimensione="piccolo" disabled title={dati.motivo ?? undefined}
               aria-label={etichetta} aria-describedby={idMotivo}>{testo}</Bottone>
      ))
  // L'ultimo modulo firmato ricevuto e ancora in verifica antivirus: vale, e la riga lo dice.
  const inVerifica = !!p.firmato_il && !!p.firmati?.some((f) => f.data_firma === p.firmato_il && f.antivirus === 'in_attesa')
  const cidis = dati.cidis

  return (
    <section className="modulo-progetto-firme" aria-label={`Moduli del progetto ${p.nome_breve}`}>
      <p>
        <strong>{p.nome_breve}</strong>{' '}
        <span className="tenue piccolo">{p.codice}{p.titolo !== p.nome_breve ? ` · ${p.titolo}` : ''}</span>
        {!p.aperto && <span className="tenue piccolo"> · percorsi chiusi</span>}
      </p>
      <ul className="modulo-firmati">
        <li>
          <span className="riga">
            <Etichetta tono={p.firmato_il ? 'positivo' : 'attenzione'}>
              Modulo {p.nome_breve}: {p.firmato_il ? `firmato il ${giorno(p.firmato_il)}` : 'da firmare'}
            </Etichetta>
            {inVerifica && <span className="tenue piccolo">{ETICHETTA_IN_VERIFICA}</span>}
          </span>
          <span className="modulo-firma-comandi">
            {stampa(`/persone/${personaId}/moduli-progetto/${p.id}`, `Stampa il modulo ${p.nome_breve}`)}
            {dati.caricare && (
              <Bottone variante="secondario" dimensione="piccolo" icona={<IconaCarica />} onClick={caricaProgetto}
                       aria-label={`Carica il modulo ${p.nome_breve} firmato`}>
                Carica firmato
              </Bottone>
            )}
          </span>
        </li>
        <li>
          {cidis
            ? <StatoCidis cidis={cidis} />
            : <span className="tenue piccolo">Modulo CIDIS (consensi): lo stato si vede con la lettura dei consensi della scheda.</span>}
          <span className="modulo-firma-comandi">
            {stampa(`/persone/${personaId}/stampa?progetto=${p.id}`, 'Stampa il modulo CIDIS dei consensi')}
            {cidis?.stato === 'da_firmare' && registraConsensi && (
              <Bottone variante="secondario" dimensione="piccolo" onClick={registraConsensi}>Registra i consensi</Bottone>
            )}
            {cidis?.scansione === 'da_caricare' && caricaScansione && (
              <Bottone variante="secondario" dimensione="piccolo" icona={<IconaCarica />} onClick={caricaScansione}
                       aria-label="Carica il modulo CIDIS firmato">
                Carica firmato
              </Bottone>
            )}
          </span>
        </li>
      </ul>
      <div className="riga">
        {stampa(`/persone/${personaId}/moduli-progetto/${p.id}/due-moduli`,
          `Stampa i due moduli: ${p.nome_breve} e CIDIS`, 'Stampa i due moduli')}
        <span className="tenue piccolo">Il modulo {p.nome_breve} e poi il modulo CIDIS, nella stessa stampa.</span>
      </div>
    </section>
  )
}

/** «Modulo CIDIS (consensi): firmato il … / da firmare», con la scansione se manca. */
function StatoCidis({ cidis }: { cidis: StatoModuloCidis }) {
  return (
    <span className="riga">
      <Etichetta tono={cidis.stato === 'firmato' ? 'positivo' : 'attenzione'}>
        Modulo CIDIS (consensi): {cidis.stato === 'firmato'
          ? `firmato${cidis.data ? ` il ${giorno(cidis.data)}` : ''}` : 'da firmare'}
      </Etichetta>
      {cidis.scansione === 'da_caricare' && (
        <span className="tenue piccolo">Scansione non ancora caricata: è facoltativa, i consensi valgono come registrati.</span>
      )}
    </span>
  )
}

/** Progetto, data della firma e file: il documento nasce legato al progetto (rotte/documenti.ts). */
function CaricaFirmato({ personaId, dati, iniziale, annulla, fatto }: {
  personaId: string; dati: ModuliDellaScheda; iniziale: string; annulla: () => void; fatto: (testo: string) => void
}) {
  const [progetto, setProgetto] = useState(iniziale)
  const [dataFirma, setDataFirma] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [errori, setErrori] = useState<{ data?: string; file?: string }>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const idFile = useId()
  const p = dati.progetti.find((x) => x.id === progetto) ?? dati.progetti[0]

  async function invia() {
    const nuovi = {
      data: !dataFirma ? 'Indica la data della firma.' : dataFirma > oggi() ? 'La data della firma non può essere futura.' : undefined,
      file: file ? undefined : 'Scegli il file del modulo firmato.',
    }
    setErrori(nuovi); setErrore(null)
    if (nuovi.data || nuovi.file || !file) return
    setInCorso(true)
    try {
      await caricaDocumento(personaId, {
        nome: file.name, tipo: TIPO_MODULO_FIRMATO, dati: file, data_documento: dataFirma, progetto_id: p.id,
      })
      fatto(`Modulo ${p.nome_breve} firmato il ${giorno(dataFirma)}: caricato e salvato. ${attesaAntivirus(new Date()).verifica}`)
    } catch (e) {
      setErrore(messaggio(e))
    } finally { setInCorso(false) }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); void invia() }}>
      {dati.progetti.length > 1 && (
        <Scelta etichetta="Progetto" valore={p.id} cambia={setProgetto} obbligatorio
                opzioni={dati.progetti.map((x) => ({ valore: x.id, etichetta: `${x.codice} · ${x.titolo}` }))} />
      )}
      {dati.progetti.length === 1 && <p>Modulo del progetto <strong>{p.codice} · {p.titolo}</strong>.</p>}
      <p className="nota-modulo">
        Il modulo del progetto, con l’informativa del suo titolare. Il modulo CIDIS dei consensi si carica dalla sua riga.
      </p>
      <Campo etichetta="Data della firma" tipo="date" valore={dataFirma} cambia={setDataFirma} max={oggi()} obbligatorio
             errore={errori.data} aiuto="Quella scritta accanto alla firma sul modulo." />
      <div className={`campo${errori.file ? ' campo-errato' : ''}`}>
        <label htmlFor={idFile}>Modulo firmato<span className="campo-obbligatorio" aria-hidden="true"> *</span></label>
        <input id={idFile} type="file" accept="application/pdf,image/*" aria-describedby={`${idFile}-nota`}
               onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        {errori.file
          ? <p className="campo-errore" id={`${idFile}-nota`} role="alert">{errori.file}</p>
          : <p className="campo-aiuto" id={`${idFile}-nota`}>
              {file ? `${file.name} · ${Math.ceil(file.size / 1024)} kB` : 'La scansione del modulo firmato, in PDF o come immagine.'}
            </p>}
      </div>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" icona={<IconaCarica />} inCorso={inCorso}>Carica</Bottone>
      </div>
    </form>
  )
}
